// Canonical ENGLISH display labels for server enums (Conv-30).
//
// The i18n design keys on the English source string, so an enum can only ever be
// translated through a fixed English label: `t(assetClassLabel(c))`. The failure mode
// this replaces is the ad-hoc `c.replace(/_/g, ' ').toLowerCase()` that four pages
// used — it derives the label at runtime, so the string never appears in the source,
// no catalog can ever contain it, and the column renders "indian equity" in every
// language. There is no gate that can see that; a derived label is invisible by
// construction.
//
// It also de-duplicates: four separate CLASS_LABELS maps had drifted across
// ImportReview / AssetGrid / Portfolio / Household. One map, one spelling.
//
// RULE: never build a user-facing label by transforming an enum. Add it here.

/** Asset classes — mirrors ASSET_CLASSES in server/routes-extended.mjs. */
export const ASSET_CLASS_LABELS: Record<string, string> = {
  INDIAN_EQUITY: 'Indian equity',
  US_EQUITY: 'US equity',
  MUTUAL_FUND: 'Mutual fund',
  ETF: 'ETF',
  GOLD: 'Gold',
  COMMODITY: 'Commodity (MCX)',
  FIXED_DEPOSIT: 'Fixed deposit',
  BONDS: 'Bonds',
  REAL_ESTATE: 'Real estate',
  FRACTIONAL_REAL_ESTATE: 'Fractional real estate',
  CRYPTO_DISCLOSURE_ONLY: 'Crypto (disclosure only)',
  CASH: 'Cash',
  OTHER: 'Other',
};

// PRIVATE_INVESTMENT is a SYNTHETIC allocation class — A1 folds the F98 ledger into
// the summary — so it is deliberately absent from ASSET_CLASS_LABELS, which seeds the
// add-asset dropdown. A private investment is added through its own ledger, never as
// a manual holding. Keep it out of the map and in the fallback.
export const assetClassLabel = (c: string): string =>
  ASSET_CLASS_LABELS[c] ?? (c === 'PRIVATE_INVESTMENT' ? 'Private investment' : c);

/**
 * Conv-48 · B — what KIND of account a cash/deposit balance sits in.
 *
 * These are CANONICAL ENGLISH LABELS and they live here for the reason every
 * other entry in this file does: a label derived by transforming the enum
 * (`'NRE'.toLowerCase()`) is a string that exists nowhere in the source, so no
 * catalog can contain it and NEITHER i18n gate can see it — that is worse than an
 * untranslated literal, and Conv-30 shipped it on four pages.
 *
 * 🔴 THE EXPANSIONS ARE DESCRIPTIVE AND MUST STAY DESCRIPTIVE. "Non-Resident
 * External" is what the acronym stands for; it is a fact about the name. What may
 * never appear here — or anywhere downstream — is what the account TYPE means for
 * repatriation, tax or remittance limits. Those are FEMA and Income-Tax questions,
 * and answering them is advice from an entity holding no registration to give it.
 * The user told Sansara what the account is; Sansara shows the word back.
 */
export const ACCOUNT_NATURE_LABELS: Record<string, string> = {
  RESIDENT: 'Resident',
  NRE: 'NRE (Non-Resident External)',
  NRO: 'NRO (Non-Resident Ordinary)',
  FCNR: 'FCNR (Foreign Currency Non-Resident)',
};

export const accountNatureLabel = (n: string): string => ACCOUNT_NATURE_LABELS[n] ?? n;

/**
 * What KIND of crypto or cash holding a row is (server HOLDING_KINDS) — the
 * user's own label, keyed by class. A LABEL ONLY: a stablecoin stays a crypto
 * holding (taxed as one, never treated as cash, never priced at 1 USD), and an
 * e-rupee balance is rupee cash issued by the RBI.
 */
export const HOLDING_KINDS_BY_CLASS: Record<string, string[]> = {
  CRYPTO_DISCLOSURE_ONLY: ['STABLECOIN_USD', 'STABLECOIN_INR', 'OTHER_VDA'],
  CASH: ['E_RUPEE'],
};
export const HOLDING_KIND_LABELS: Record<string, string> = {
  STABLECOIN_USD: 'Stablecoin (US dollar-referenced)',
  STABLECOIN_INR: 'Stablecoin (rupee-referenced)',
  OTHER_VDA: 'Other crypto',
  E_RUPEE: 'e-rupee (RBI CBDC)',
};
export const holdingKindLabel = (k: string): string => HOLDING_KIND_LABELS[k] ?? k;

// Conv-56 fix 40 — the mutual-fund TYPE, the user's own declaration. Canonical
// English labels; render through t(fundTypeLabel(x)) like every other enum.
export const FUND_TYPE_LABELS: Record<string, string> = {
  EQUITY: 'Equity fund',
  DEBT: 'Debt fund',
  HYBRID: 'Hybrid fund',
  GOLD: 'Gold fund',
};
export const FUND_TYPES = Object.keys(FUND_TYPE_LABELS);
export const fundTypeLabel = (n: string): string => FUND_TYPE_LABELS[n] ?? n;

// How a property is USED — the holder's own answer, a label only. Applies to
// REAL_ESTATE / FRACTIONAL_REAL_ESTATE; render through t(propertyUseLabel(x)).
export const PROPERTY_USE_LABELS: Record<string, string> = {
  SELF_OCCUPIED: 'My home (I live in it)',
  LET_OUT: 'Let out to a tenant',
};
export const PROPERTY_USES = Object.keys(PROPERTY_USE_LABELS);
export const propertyUseLabel = (n: string): string => PROPERTY_USE_LABELS[n] ?? n;

// Conv-57 (F10) — budget expense categories. The closed set the server's
// routes-budget.mjs accepts (BUDGET_CATEGORIES). Canonical English labels;
// render through t(budgetCategoryLabel(x)) like every other enum.
export const BUDGET_CATEGORY_LABELS: Record<string, string> = {
  Food: 'Food',
  Transport: 'Transport',
  Housing: 'Housing',
  Utilities: 'Utilities',
  Entertainment: 'Entertainment',
  Health: 'Health',
  Education: 'Education',
  Shopping: 'Shopping',
  Travel: 'Travel',
  Other: 'Other',
};
export const BUDGET_CATEGORIES = Object.keys(BUDGET_CATEGORY_LABELS);
export const budgetCategoryLabel = (c: string): string => BUDGET_CATEGORY_LABELS[c] ?? c;

/** Subscription tier display names — mirrors TIER_NAMES in server/sansara-server.mjs. */
// Conv-37: no FREE entry — the tier no longer exists server-side, and mapping a
// stale key to "Free" would name a plan nobody can be on.
export const TIER_LABELS: Record<string, string> = {
  SILVER_ELITE: 'Silver Elite',
  GOLD_ELITE: 'Gold Elite',
  PLATINUM_ELITE: 'Platinum Elite',
};

// The server sends the DISPLAY name ("Gold Elite"), not the key, on
// subscription.name / entitlements.featureTiers / plans[].name. Passing it straight
// to t() is correct — the English name IS the key — this helper just makes the intent
// explicit at the call site and tolerates a key arriving instead of a name.
// Billing rows that are not a tier: the CA/IFA practice seat (server/ca-seat.mjs)
// settles through the same order history, so its plan id needs a name there too.
// Kept OUT of TIER_LABELS, which describes the consumer ladder.
export const PRODUCT_LABELS: Record<string, string> = {
  CA_SEAT: 'Practice seat',
};
export const tierLabel = (nameOrKey: string): string => TIER_LABELS[nameOrKey] ?? PRODUCT_LABELS[nameOrKey] ?? nameOrKey;

/**
 * True when this tier is the TOP of the ladder, by KEY or by DISPLAY NAME.
 *
 * Conv-40, founder: every paywall read "{tier} or higher", which on the dearest
 * plan promised a tier that does not exist — "Platinum Elite or higher" sends the
 * reader looking for something above Platinum. "Gold Elite or higher" is true and
 * stays.
 *
 * The ordering is NOT re-declared here. `TIER_ORDER` already lives in
 * `useEntitlements.ts` (itself a mirror of the server's), and a second copy in this
 * file would be a third opinion about the ladder — exactly the drift that let
 * "Free < Silver < Gold < Platinum" survive three deploys past the deletion of
 * FREE. The top tier is passed in by the caller, which already holds that list.
 *
 * Unknown values return FALSE, keeping the "or higher" wording: imprecise is a
 * safer failure than denying the existence of a plan someone could actually buy.
 */
export const isTopTier = (nameOrKey: string, topKey: string): boolean =>
  nameOrKey === topKey || nameOrKey === TIER_LABELS[topKey];

/** KYC status values written by the server (sansara-server.mjs). */
export const KYC_STATUS_LABELS: Record<string, string> = {
  'Not started': 'Not started',
  Verified: 'Verified',
  Pending: 'Pending',
  Rejected: 'Rejected',
};

export const kycStatusLabel = (s: string): string => KYC_STATUS_LABELS[s] ?? s;

/** Billing order status + cycle, rendered as chips on Plans. */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  CREATED: 'Created',
  PAID: 'Paid',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  // C25(b) — outcomes of a founder payment review. Same words as the plain
  // states (already translated in every locale): to the user, the money was
  // either taken for the plan or given back.
  PAID_AFTER_REVIEW: 'Paid',
  REFUNDED_AFTER_REVIEW: 'Refunded',
};

export const BILLING_CYCLE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  halfYearly: 'Half-yearly',
  yearly: 'Yearly',
};

/** Options legs. */
export const OPTION_LEG_LABELS: Record<string, string> = {
  call: 'Call',
  put: 'Put',
};

/** Property types + city tiers on the (dark) property partner screen. */
export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  RESIDENTIAL: 'Residential',
  COMMERCIAL: 'Commercial',
  PLOT: 'Plot',
};

export const CITY_TIER_LABELS: Record<string, string> = {
  METRO: 'Metro',
  TIER1: 'Tier 1',
  TIER2: 'Tier 2',
  TIER3: 'Tier 3',
};

/** Research/analyst pipeline stages (dark shell). */
export const PIPELINE_STAGE_LABELS: Record<string, string> = {
  drafts: 'Drafts',
  inReview: 'In review',
  published: 'Published',
};

// Price alerts — the kind and the lifecycle status, rendered as a chip on Desk
// (which printed the raw `PRICE · ARMED` enums). The spellings mirror the
// Markets alert form so one alert never reads as two different things on two
// screens.
export const ALERT_KIND_LABELS: Record<string, string> = {
  PRICE: 'Price level',
  PERCENT: 'Percent move',
};

export const ALERT_STATUS_LABELS: Record<string, string> = {
  ARMED: 'Armed',
  TRIGGERED: 'Triggered',
};

export const alertKindLabel = (k: string): string => ALERT_KIND_LABELS[k] ?? k;
export const alertStatusLabel = (s: string): string => ALERT_STATUS_LABELS[s] ?? s;

// Consent-link lifecycle — ONE spelling shared by F84 access grants and F123
// joint links, which are the same idea (an invitation someone accepts, declines
// or revokes) and rendered as a chip on Household. Both printed the raw
// `PENDING` / `ACTIVE` enum.
export const LINK_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  REVOKED: 'Revoked',
  DECLINED: 'Declined',
};

export const linkStatusLabel = (s: string): string => LINK_STATUS_LABELS[s] ?? s;

/** Holdings-statement (proof-of-wealth) lifecycle, rendered as a chip on Household. */
export const PROOF_STATUS_LABELS: Record<string, string> = {
  VALID: 'Valid',
  EXPIRED: 'Expired',
  REVOKED: 'Revoked',
};

export const proofStatusLabel = (s: string): string => PROOF_STATUS_LABELS[s] ?? s;

/**
 * F44 holding period. `term` is the pure module's own enum ('SHORT' | 'LONG')
 * and decides which statutory rate applies, so it must read as a term of art,
 * not as a judgement — neither one is the good outcome.
 */
export const TAX_TERM_LABELS: Record<string, string> = {
  SHORT: 'Short term',
  LONG: 'Long term',
};

export const taxTermLabel = (v: string): string => TAX_TERM_LABELS[v] ?? v;

/**
 * F44 exclusion reasons — why a matched parcel carries NO tax figure.
 *
 * These are the four refusals made visible, and every one of them must render:
 * a silently dropped parcel is a tax figure that is quietly too low. Note that
 * TAX_TREATMENT_UNKNOWN and NON_EQUITY_REGIME are DIFFERENT states and must not
 * share copy — the first is an unanswered question the user can fix, the second
 * is the user's own completed answer putting the lot outside s.111A/112A.
 * Merging them either nags someone who already answered or hides a fixable gap.
 */
export const TAX_EXCLUSION_LABELS: Record<string, string> = {
  NO_ACQUISITION_DATE: 'You have not recorded when you bought this',
  UNUSABLE_DATES: 'The purchase and sale dates do not form a holding period',
  TAX_TREATMENT_UNKNOWN: 'Tell us whether this holding is equity-oriented',
  NON_EQUITY_REGIME: 'You marked this as not equity-oriented',
  GRANDFATHER_FMV_REQUIRED: 'Bought before 31 January 2018 — enter that day’s value',
  // Paper-gains view only: a holding with no recorded cost has no gain to show.
  NO_COST_BASIS: 'You have not recorded what you paid',
  OUTSIDE_EQUITY_RULE: 'Not split: only the 12-month equity rule is applied here',
  FUTURE_PURCHASE_DATE: 'The purchase date you recorded is after today',
  // VDA (crypto) ledger only.
  NOT_MATCHED_TO_A_HOLDING: 'No matching holding left to sell from',
};

export const taxExclusionLabel = (v: string): string => TAX_EXCLUSION_LABELS[v] ?? v;

/** How often a scheduled drift check runs (server CADENCES). The user picks; there is no default. */
export const CADENCE_LABELS: Record<string, string> = {
  WEEKLY: 'Every week',
  MONTHLY: 'Every month',
  QUARTERLY: 'Every quarter',
};

export const cadenceLabel = (v: string): string => CADENCE_LABELS[v] ?? v;

/**
 * Household / access-grant roles. The web consent-ledger rendered these as
 * `t(f.role.toLowerCase())` — a DERIVED label, which is worse than an
 * untranslated literal: the string `'owner'` exists nowhere in the source, so no
 * catalog can hold it and NEITHER i18n gate can see it. A Hindi reader got
 * "आपकी भूमिका: owner" on the one screen whose whole claim is that they can see
 * everything with access to their money. The mobile twin already did this
 * correctly, with a comment saying why; this is that fix reaching web.
 */
export const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  MEMBER: 'Member',
  VIEWER: 'Viewer',
};

export const roleLabel = (r: string): string => ROLE_LABELS[String(r).toUpperCase()] ?? r;

/**
 * Access-grant SCOPES. Same class as the roles above and found the same way: the
 * consent ledger rendered `t(sc)` over the raw server enums SUMMARY / HOLDINGS /
 * GOALS, so a Hindi reader saw them in raw English uppercase beside a grantee's
 * email — on the screen whose whole claim is that they can see everything with
 * access to their money. `SCOPE_LABELS` already existed in Household.tsx; it
 * belongs here, where both pages and both i18n gates can reach it.
 */
export const ACCESS_SCOPE_LABELS: Record<string, string> = {
  SUMMARY: 'Totals & allocation',
  HOLDINGS: 'Holdings list',
  GOALS: 'Goals',
  TAX: 'Capital gains',
};

export const accessScopeLabel = (sc: string): string => ACCESS_SCOPE_LABELS[String(sc).toUpperCase()] ?? sc;

/**
 * Entity kinds (F83) — mirrors ENTITY_KINDS in server/routes-structure.mjs, plus
 * the one kind only the server creates: MINOR_GUARDIAN_MANAGED, the under-18
 * account a parent or guardian manages (server/minor-accounts.mjs). A user cannot
 * give their own entity that kind; it exists only while a guardian link does.
 */
export const ENTITY_KIND_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  HUF: 'HUF',
  LLP: 'LLP',
  PRIVATE_LIMITED: 'Private Ltd',
  PARTNERSHIP: 'Partnership',
  TRUST: 'Trust',
  OTHER: 'Other',
  MINOR_GUARDIAN_MANAGED: 'Minor (guardian-managed)',
};

export const entityKindLabel = (k: string): string => ENTITY_KIND_LABELS[k] ?? k;

/**
 * Under-18 account statuses (server/minor-accounts.mjs MINOR_STATUS), as the
 * guardian and the minor read them.
 */
export const MINOR_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Waiting for approval',
  ACTIVE: 'Active',
  FROZEN: 'Frozen',
  TURNED_18: 'Turned 18',
};

export const minorStatusLabel = (s: string): string => MINOR_STATUS_LABELS[s] ?? s;

// Every label above is a plain English sentence fragment, so `check:i18n` can resolve
// it as a live source string and `check:i18n-coverage` can require a translation for
// it in every locale. That is the whole point of listing them: a label that exists
// only as a runtime transform is unreachable by both gates.
